import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { commaFormatNumber } from '@Utils/String';
import { mapContextToTitle } from '@Containers/ResultToolbar/ResultToolbarConstants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';

import Page from '@Containers/Page';
import BreadcrumbsContainer from '@Containers/Breadcrumbs';
import ResultsFilters from '@Containers/ResultsFilters';
import ResultToolbarContainer from '@Containers/ResultToolbar';
import DatabaseSwitcherTabs from '@Containers/ResultToolbar/DatabaseSwitcher';
import ResultTopPanelContainer from '@Containers/ResultToolbar/ResultTopPanel/ResultTopPanelContainer';

import ResultsContentSection from './Components/ResultsContentSection';

import './ResultsPage.scss';

export default function ResultsPage({
  stateParamsReady,
  isGridView,
  toggleGridView,
  availableDatabases,
  currentDatabase,
  isDbSwitcherVisible,
  selectDatabase,
  goToResultPage,
  supportsReactions,
  showAllPreparations,
  metadata,
  context,
  results,
  searchType,
  originalSearchType,
  currentPageSize,
  resultsLoading,
  paginationVisible,
  showConditions,
  toggleConditions,
  recordCountOrTotalResult,
  isLimitDisabled,
  limitTo,
  isExportEnabled,
  openExportDialog,
  params,
  activeBreadcrumb,

  // Selection
  isAllResultsChecked,
  isAllResultsCheckedPartial,
  setAllResultsChecked,
  setAllResultsUnchecked,
  checkedItemsNumber,
  currentPage,
  totalPages,
}) {
  const resultsWrapperId = `results-tabpanel-${context}-${metadata.databaseId}`;
  const databaseTabId = `databases-tab-${context}-${metadata.databaseId}`;
  const hasMatchingDatabaseTab =
    isDbSwitcherVisible &&
    availableDatabases?.some(
      ({ context: databaseContext, databaseId }) => `${databaseContext}` === `${context}` && `${databaseId}` === `${metadata.databaseId}`
    );
  const resultsWrapperAttrs = hasMatchingDatabaseTab
    ? {
        id: resultsWrapperId,
        role: 'tabpanel',
        'aria-labelledby': databaseTabId,
      }
    : {
        id: resultsWrapperId,
      };

  return (
    <Page
      id="results-page"
      className={cn('results-page-container', { 'grid-tiles-view': isGridView })}
      withFooter
      renderHeaderInsideScrollRoot
      headerType="main">
      {/*
        FR-4.3: `#main-content` is the single per-page content landmark and the target of the
        app-wide "Main Content" skip link (skipLinkMappings). It is a real `<main>` element that
        wraps EVERYTHING between the header (rendered inside the scroll root by `<Page>`) and the
        footer — breadcrumbs, top panel, database tabs, and the two-column filter/results row — so
        the skip link lands at the top of the page's content and the whole content area is one
        `main` landmark (mirrors Alerts/History/Profile). No static `tabindex` is needed: the Leyden
        `SkipLinks` component sets `tabindex="-1"` on the target and focuses it on activation
        (`@els/els-react--skip-links`), and focus styling comes from the `#main-content:focus-visible`
        rule in `_accessibility.scss`. The shared Page Footer stays OUTSIDE `<main>` as a
        `contentinfo` landmark of its own.
      */}
      <main id="main-content" className="results-page__main">
        <div className="results-page__prelude">
          <BreadcrumbsContainer />
          <ResultTopPanelContainer goToResultPage={goToResultPage} params={params} />
          <DatabaseSwitcherTabs
            className="u-els-margin-left-2x u-els-margin-right-2x u-els-margin-top u-els-margin-bottom"
            availableDatabases={availableDatabases}
            isDbSwitcherVisible={isDbSwitcherVisible}
            currentDatabase={currentDatabase}
            selectDatabase={selectDatabase}
          />
        </div>
        <div className={`flex-item-height-fix padded-container results-page__body ${context}-results`}>
          <ResultsFilters
            className="o-els-flex-layout__item"
            activeBreadcrumb={activeBreadcrumb}
            params={params}
            resultsLoading={resultsLoading}
          />
          <section
            id="results-container"
            className="results-container results-page__results-column results-container--substance-result-optimization o-els-flex-layout__item o-els-flex-layout__item--grow"
            aria-label={`Results: ${commaFormatNumber(recordCountOrTotalResult || '')} ${mapContextToTitle[context]}`}
            aria-busy={resultsLoading}>
            <div className="results-wrapper" {...resultsWrapperAttrs}>
              <div className="results-list-wrapper-inner">
                {/* AR-7.3: SummaryContainer now mounts once, inside the Assistant/Filters drawer
                    (ResultsFilters), not here — this was the last other mount point. */}
                {stateParamsReady && (
                  <ResultToolbarContainer
                    isListView={!isGridView}
                    togglerResultView={toggleGridView}
                    availableDatabases={availableDatabases}
                    isDbSwitcherVisible={isDbSwitcherVisible}
                    currentDatabase={currentDatabase}
                    selectDatabase={selectDatabase}
                    goToResultPage={goToResultPage}
                    supportsReactions={supportsReactions}
                    showAllPreparations={showAllPreparations}
                    showConditions={showConditions}
                    toggleConditions={toggleConditions}
                    checkAllItems={setAllResultsChecked}
                    uncheckAllItems={setAllResultsUnchecked}
                    allItemsSelected={isAllResultsChecked}
                    someItemsSelected={isAllResultsCheckedPartial}
                    currentSelectedItems={checkedItemsNumber}
                    disableLimit={isLimitDisabled}
                    limitTo={limitTo}
                    isExportEnabled={isExportEnabled}
                    openExportDialog={openExportDialog}
                  />
                )}

                <ResultsContentSection
                  metadata={metadata}
                  context={context}
                  results={results}
                  gridView={isGridView}
                  searchType={searchType}
                  originalSearchType={originalSearchType}
                  currentPageSize={currentPageSize}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  resultsLoading={resultsLoading}
                  paginationVisible={paginationVisible}
                  activeBreadcrumb={activeBreadcrumb}
                />
              </div>
            </div>
          </section>
        </div>
      </main>
    </Page>
  );
}

ResultsPage.propTypes = {
  stateParamsReady: PropTypes.bool,
  isGridView: PropTypes.bool,
  toggleGridView: PropTypes.func.isRequired,
  availableDatabases: PropTypes.array,
  currentDatabase: PropTypes.object,
  isDbSwitcherVisible: PropTypes.bool,
  selectDatabase: PropTypes.func.isRequired,
  goToResultPage: PropTypes.func.isRequired,
  supportsReactions: PropTypes.bool,
  showAllPreparations: PropTypes.func.isRequired,
  metadata: MetadataPropTypesShape,
  context: PropTypes.string,
  results: PropTypes.array.isRequired,
  searchType: PropTypes.string,
  originalSearchType: PropTypes.string,
  currentPageSize: PropTypes.number,
  resultsLoading: PropTypes.bool,
  paginationVisible: PropTypes.bool,
  showConditions: PropTypes.bool,
  toggleConditions: PropTypes.func.isRequired,
  recordCountOrTotalResult: PropTypes.number,
  isLimitDisabled: PropTypes.bool,
  limitTo: PropTypes.func.isRequired,
  isExportEnabled: PropTypes.bool,
  openExportDialog: PropTypes.func.isRequired,

  // Selection
  isAllResultsChecked: PropTypes.bool,
  isAllResultsCheckedPartial: PropTypes.bool,
  setAllResultsChecked: PropTypes.func.isRequired,
  setAllResultsUnchecked: PropTypes.func.isRequired,
  checkedItemsNumber: PropTypes.number,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
};
